import { useState, useEffect, useMemo } from 'react'
import { useUser } from './auth'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Badge, Menu, Layout, Select, Space, Modal } from 'antd'
import {
  BellOutlined,
  LogoutOutlined,
  SwapOutlined
} from '@ant-design/icons'
import * as api from './lib/api'
import { useI18n } from './i18n/I18nProvider'
import NotificationDashboard from './appraisals/NotificationDashboard'
import { isVietnamNameOrder } from './appraisals/HRDashboard'
import Title from 'antd/es/typography/Title'

const { Header } = Layout

function nameInOrder(u, lastNameFirst) {
  const parts = lastNameFirst ? [u.last_name, u.first_name] : [u.first_name, u.last_name]
  return parts.filter(Boolean).join(' ').trim()
}

function userLabel(u) {
  const name = nameInOrder(u, isVietnamNameOrder(u.session_template))
  return [
    name || u.email,
    u.company_employee_id
  ].filter(Boolean).join(' - ')
}

function switcherOption(u) {
  const lastNameFirst = isVietnamNameOrder(u.session_template)
  return {
    value: u.id,
    label: userLabel(u),
    sessionTemplate: u.session_template,
    // Display order first; the other order stays searchable too.
    searchText: [
      nameInOrder(u, lastNameFirst),
      nameInOrder(u, !lastNameFirst),
      u.company_employee_id,
      u.email
    ].filter(Boolean).join(' ').toLowerCase()
  }
}

function switcherContextFromPath(pathname) {
  if (pathname === '/dashboard') return { hrOnly: true }
  const sessionMatch = pathname.match(/^\/sessions\/(\d+)(?:\/|$)/)
  if (sessionMatch) return { sessionId: sessionMatch[1] }
  const appraisalMatch = pathname.match(/^\/appraisals\/(\d+)(?:\/|$)/)
  if (appraisalMatch) return { appraisalId: appraisalMatch[1] }
  return { languageDisabled: true }
}

export default function NavBar() {
  const user = useUser()
  const location = useLocation()
  const navigate = useNavigate()
  const { locale, setLocale, t } = useI18n()
  const [switching, setSwitching] = useState(false)
  const [switcherLoading, setSwitcherLoading] = useState(false)
  const [testUsers, setTestUsers] = useState([])
  const [me, setMe] = useState(null)
  const [notificationCount, setNotificationCount] = useState(0)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [languageOptions, setLanguageOptions] = useState([])
  const switcherContext = useMemo(() => switcherContextFromPath(location.pathname), [location.pathname])
  const routeLocationKey = `${location.key}:${location.pathname}${location.search}${location.hash}`
  const currentUser = me?.email ? me : user
  const currentUserId = currentUser?.id
  const currentUserEmail = currentUser?.email || ''
  const currentUserName = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ').trim() || currentUserEmail
  const canUseUserSwitcher = Boolean(user && currentUser?.can_hijack_users)
  useEffect(() => {
    let ignore = false
    if (user) {
      api.getMe().then(data => {
        if (!ignore) setMe(data)
      }).catch(err => {
        if (ignore) return
        console.error('Failed to load current user locale scope:', err)
        setMe({ email: user.email, regions: [] })
      })
    } else {
      setMe(null)
    }
    return () => {
      ignore = true
    }
  }, [routeLocationKey, user])

  useEffect(() => {
    let ignore = false
    if (!user || !canUseUserSwitcher) {
      setTestUsers([])
      setSwitcherLoading(false)
      return
    }

    const loadSwitcherUsers = () => {
      setSwitcherLoading(true)
      api.getDevUsers(switcherContext).then(users => {
        if (!ignore) setTestUsers(users.map(switcherOption))
      }).catch(err => {
        if (!ignore) console.error('Failed to load users for switching:', err)
      }).finally(() => {
        if (!ignore) setSwitcherLoading(false)
      })
    }

    loadSwitcherUsers()
    window.addEventListener('dev-users.updated', loadSwitcherUsers)
    return () => {
      ignore = true
      window.removeEventListener('dev-users.updated', loadSwitcherUsers)
    }
  }, [canUseUserSwitcher, switcherContext, user])

  useEffect(() => {
    let ignore = false
    const handleNotificationsUpdated = (event) => {
      if (typeof event.detail?.unread_total === 'number') {
        setNotificationCount(event.detail.unread_total)
      }
    }
    window.addEventListener('notifications.updated', handleNotificationsUpdated)
    if (user) {
      api.getNotifications().then(data => {
        if (!ignore) setNotificationCount(data.unread_total || 0)
      }).catch(() => {
        if (!ignore) setNotificationCount(0)
      })
    } else {
      setNotificationCount(0)
    }
    return () => {
      ignore = true
      window.removeEventListener('notifications.updated', handleNotificationsUpdated)
    }
  }, [location.pathname, user])

  useEffect(() => {
    let ignore = false
    if (!user) {
      setLanguageOptions([])
      return
    }
    if (switcherContext.languageDisabled) {
      setLanguageOptions([])
      setLocale('en')
      return
    }
    api.getLanguageOptions(switcherContext).then(payload => {
      if (ignore) return
      setLanguageOptions(payload?.enabled ? (payload.locales || []) : [])
      if (!payload?.enabled) setLocale('en')
    }).catch(() => {
      if (!ignore) {
        setLanguageOptions([])
        setLocale('en')
      }
    })
    return () => {
      ignore = true
    }
  }, [setLocale, switcherContext, user])

  useEffect(() => {
    if (!languageOptions.length) return
    const queryLocale = new URLSearchParams(location.search).get('lang')
    if (queryLocale && languageOptions.some(item => item.code === queryLocale)) {
      if (queryLocale !== locale) setLocale(queryLocale)
    } else if (!languageOptions.some(item => item.code === locale)) {
      setLocale('en')
    }
  }, [languageOptions, locale, location.search, setLocale])

  const switcherOptions = useMemo(() => {
    if (!currentUserEmail) return testUsers
    // The current user isn't in the API payload's template, so borrow it from their list entry.
    const listedCurrent = testUsers.find(option => option.value === currentUserId)
    const currentOption = {
      ...switcherOption({ ...currentUser, session_template: listedCurrent?.sessionTemplate }),
      disabled: true
    }
    const otherOptions = testUsers.filter(option => option.value !== currentUserId)
    return [currentOption, ...otherOptions]
  }, [currentUser, currentUserId, currentUserEmail, testUsers])

  const handleUserSwitch = async (userId) => {
    if (userId === currentUserId) return
    setSwitching(true)
    try {
      if (currentUser?.is_hijacked) await api.releaseHijackedUser()
      await api.hijackUser(userId)
      window.location.href = '/dashboard'
    } catch (err) {
      console.error('Failed to switch user:', err)
      window.alert(err.message || t('nav.switch_user_failed', 'Failed to switch user'))
      setSwitching(false)
    }
  }

  const rightItems = []
  if (user) {
    rightItems.push(
      {
        key: '/notifications',
        onClick: () => setNotificationsOpen(true),
        icon: (
          <Badge count={notificationCount} size='small' offset={[2, -2]}>
            <BellOutlined style={{ fontSize: 16 }} />
          </Badge>
        ),
        label: t('nav.notifications', 'Notifications')
      }
    )
    rightItems.push({
      key: 'current-user-name',
      disabled: true,
      label: <span aria-label='Current user'>{currentUserName}</span>
    })
  }
  if (user) {
    rightItems.push({
      key: '/account/logout',
      icon: <LogoutOutlined />,
      label: <Link to='/account/logout'>{t('nav.logout', 'Logout')}</Link>
    })
  }

  return (
    <>
      <Header style={{ background: 'var(--neutral-white)', padding: '0 24px', display: 'flex', alignItems: 'center', height: 48, lineHeight: '48px' }}>
        <Link to='/' aria-label={t('app.title', 'TTI Appraisal')} style={{ display: 'flex', alignItems: 'center', marginRight: 32, whiteSpace: 'nowrap' }}>
          <img src='/TTI_logo.svg' alt={t('app.title', 'TTI Appraisal')} style={{ display: 'block', height: 32, width: 'auto' }} />
        </Link>

        {/* User Switcher (POC/Testing) */}
        {user && canUseUserSwitcher && (
          <div style={{ marginRight: 16 }}>
            <Space size='small'>
              <SwapOutlined style={{ color: 'var(--tti-light-grey)', fontSize: 12 }} />
              <Select
                aria-label={t('nav.switch_user', 'Switch user')}
                value={currentUserId}
                onChange={handleUserSwitch}
                loading={switching || switcherLoading}
                disabled={switching}
                style={{ width: 420 }}
                size='small'
                showSearch
                filterOption={(input, option) => (option?.searchText || '').includes(input.trim().toLowerCase())}
                options={switcherOptions}
              />
            </Space>
          </div>
        )}

        <Menu
          mode='horizontal'
          selectedKeys={[location.pathname]}
          items={[...rightItems]}
          style={{ flex: 1, border: 'none', minWidth: 0, justifyContent: 'flex-end', fontSize: 13, lineHeight: '46px' }}
        />
      </Header>
      <Modal
        title={<>{<Title style={{ display: 'inline-block', margin: 0 }} level={4}>{t('nav.notifications', 'Notifications')}</Title>}</>}
        open={notificationsOpen}
        onCancel={() => setNotificationsOpen(false)}
        footer={null}
        width={800}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        {notificationsOpen && (
          <NotificationDashboard
            embedded
            onNavigate={url => {
              setNotificationsOpen(false)
              navigate(url)
            }}
          />
        )}
      </Modal>
    </>
  )
}
